/**
 * TagSearchCombobox — searchable tag picker for the binding workspace (P6.5).
 *
 * Replaces the flat data_kind-filtered `<select>`: the user types and the
 * list filters by **Thing name OR tag name**, with options grouped under
 * their Thing. Only data_kind-compatible tags are offered (the caller
 * pre-filters), and already-bound tags are excluded upstream too.
 *
 * `TagOption.thing_name` / `thing_slug` (from `GET /v1/iot/tags`, P6.5) drive
 * the group headers + the secondary line on each option. Selecting a tag
 * calls `onSelect(tagId)` and clears the field.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Cpu } from "lucide-react";
import type { DataKind } from "@foundry/foundry-plant";
import type { TagOption } from "@/lib/api";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";

const UNGROUPED = "Other tags";

/**
 * The numeric data-kind family: float / int / percentage / KPI are all
 * mutually convertible, so a slot of any of these accepts a tag of any
 * other. Every other kind (categorical, event, boolean, string,
 * distribution) is incompatible across kinds and matches only itself.
 */
const NUMERIC_KINDS: ReadonlySet<DataKind> = new Set<DataKind>([
  "analog_scalar",
  "analog_percentage",
  "count_scalar",
  "derived_kpi",
]);

/**
 * The set of tag data-kinds that may bind to a slot of `slotKind`.
 * Compatible = SAME kind, OR (when the slot is numeric) any other numeric
 * kind. Non-numeric kinds only match themselves. Used to filter the binding
 * candidate list so incompatible tags are never offered.
 */
export function compatibleKinds(slotKind: DataKind): Set<DataKind> {
  if (NUMERIC_KINDS.has(slotKind)) return new Set(NUMERIC_KINDS);
  return new Set<DataKind>([slotKind]);
}

export interface TagSearchComboboxProps {
  /** Pre-filtered, compatible, not-yet-bound tags. */
  tags: TagOption[];
  onSelect: (tagId: string) => void;
  placeholder?: string;
  /** Shown (disabled) when there are zero compatible tags. */
  emptyLabel?: string;
  /**
   * Tags filtered out for a data-kind mismatch. Rendered DISABLED (not
   * selectable) under a "Not compatible" header with a reason, so the user
   * sees *why* a tag isn't offered instead of it silently vanishing.
   */
  incompatibleTags?: TagOption[];
  /** Why incompatibleTags can't be picked (e.g. "needs a numeric tag"). */
  incompatibleReason?: string;
}

interface Group {
  key: string;
  label: string;
  tags: TagOption[];
}

export function TagSearchCombobox({
  tags,
  onSelect,
  placeholder = "Search tags by Thing or name…",
  emptyLabel = "No compatible tags",
  incompatibleTags = [],
  incompatibleReason = "incompatible data type",
}: TagSearchComboboxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const matchesQuery = (t: TagOption, needle: string) =>
    !needle ||
    t.name.toLowerCase().includes(needle) ||
    (t.thing_name ?? "").toLowerCase().includes(needle) ||
    (t.thing_slug ?? "").toLowerCase().includes(needle);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Filter by Thing name OR tag name (case-insensitive substring).
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tags.filter((t) => matchesQuery(t, needle));
  }, [tags, query]);

  // Incompatible tags matching the query — rendered disabled below.
  const incompatFiltered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return incompatibleTags.filter((t) => matchesQuery(t, needle));
  }, [incompatibleTags, query]);

  // The field is usable (and the dropdown opens) when there's anything to
  // show — compatible tags to pick OR incompatible ones to explain.
  const hasAny = tags.length > 0 || incompatibleTags.length > 0;

  // Group the filtered tags under their Thing, preserving first-seen order.
  const groups = useMemo<Group[]>(() => {
    const byKey = new Map<string, Group>();
    for (const t of filtered) {
      const key = t.thing_slug || t.thing_name || UNGROUPED;
      const label = t.thing_name || t.thing_slug || UNGROUPED;
      let g = byKey.get(key);
      if (!g) {
        g = { key, label, tags: [] };
        byKey.set(key, g);
      }
      g.tags.push(t);
    }
    return [...byKey.values()];
  }, [filtered]);

  // Flatten for keyboard navigation (group headers are not selectable).
  const flat = useMemo(() => groups.flatMap((g) => g.tags), [groups]);

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  const choose = (t: TagOption) => {
    onSelect(t.id);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-on-surface-muted" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={tags.length ? placeholder : emptyLabel}
          disabled={!hasAny}
          className="h-8 pl-8 text-xs"
          onKeyDown={(e) => {
            if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
              setOpen(true);
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, flat.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const t = flat[active];
              if (t) choose(t);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
        />
      </div>

      {open && hasAny && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-surface py-1 shadow-lg"
        >
          {flat.length === 0 && incompatFiltered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-on-surface-muted">
              No tags match “{query}”.
            </p>
          ) : (
            <>
            {groups.map((g) => (
              <div key={g.key}>
                <div className="flex items-center gap-1.5 px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                  <Cpu className="size-3" />
                  {g.label}
                </div>
                {g.tags.map((t) => {
                  const idx = flat.indexOf(t);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="option"
                      aria-selected={idx === active}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => choose(t)}
                      className={cn(
                        "flex w-full flex-col items-start px-3 py-1.5 text-left text-xs",
                        idx === active
                          ? "bg-brand-muted text-brand"
                          : "text-on-surface hover:bg-surface-muted",
                      )}
                    >
                      <span className="font-mono">
                        {t.name}
                        {t.unit ? (
                          <span className="text-on-surface-muted"> ({t.unit})</span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
            {incompatFiltered.length > 0 && (
              <div>
                <div className="px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted/70">
                  Not compatible · {incompatibleReason}
                </div>
                {incompatFiltered.map((t) => (
                  <div
                    key={t.id}
                    aria-disabled
                    title={`Can't bind here — ${incompatibleReason}`}
                    className="flex w-full cursor-not-allowed flex-col items-start px-3 py-1.5 text-left text-xs opacity-50"
                  >
                    <span className="font-mono">
                      {t.name}
                      {t.unit ? (
                        <span className="text-on-surface-muted"> ({t.unit})</span>
                      ) : null}
                      <span className="ml-1 text-on-surface-muted">· {t.data_kind}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
